/**
 * Flat, Zod-issue-driven error summary for the offer form.
 *
 * Replaces the legacy `FormErrorSummary` (which needed a nested RHF error
 * shape). Renders one human-readable line per Zod issue, mapping field paths to
 * friendly labels via `OFFER_FIELD_LABELS`.
 *
 * Aliases: offer error summary, offer form errors, zod issue summary.
 */
import type { ZodIssue } from "zod";

/** Human-readable labels for the offer form-level error summary. */
export const OFFER_FIELD_LABELS: Record<string, string> = {
  headline: "Headline",
  description: "Offer description",
  preHeadline: "Pre-headline",
  vslVideoUrl: "VSL video URL",
  applicationDeadline: "Application deadline",
  cohortStartDate: "Cohort start date",
  price: "Price",
  originalPrice: "Original price",
  currency: "Currency",
  summitId: "Summit",
  summitSessionId: "Workshop session",
  strugglesHeadline: "Challenges · headline",
  strugglesDescription: "Challenges · main text",
  strugglesImageUrl: "Challenges · image",
  resultsHeadline: "Results · headline",
  resultsPreHeadline: "Results · pre-headline",
  resultsDescription: "Results · description",
  resultsImageUrl: "Results · image",
  programHeadline: "Program · headline",
  programDescription: "Program · description",
  programImageUrl: "Program · image",
  satisfactionGuaranteeText: "Satisfaction guarantee",
  bottomCtaHeadline: "Final CTA · headline",
  bottomCtaText: "Final CTA · text",
  "tags.*.title": "Tag · title",
  "tags.*.property": "Tag · property",
  "benefits.*.title": "Benefit · title",
  "benefits.*.content": "Benefit · content",
  "benefits.*.imageSrc": "Benefit · image",
  "testimonials.*.content": "Testimonial · content",
  "testimonials.*.author": "Testimonial · author",
  "components.*.title": "Component · title",
  "components.*.content": "Component · content",
  "components.*.imageUrl": "Component · image",
  "bonuses.*.title": "Bonus · title",
  "bonuses.*.content": "Bonus · content",
  "bonuses.*.imageSrc": "Bonus · image",
  "faqs.*.question": "FAQ · question",
  "faqs.*.answer": "FAQ · answer",
};

function labelFor(dotName: string): string {
  const normalized = dotName.replace(/\.\d+\./g, ".*.");
  return (
    OFFER_FIELD_LABELS[normalized] ?? OFFER_FIELD_LABELS[dotName] ?? dotName
  );
}

export function OfferErrorSummary({ issues }: { issues: ZodIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 p-4"
      role="alert"
    >
      <p className="text-sm font-medium text-red-800">
        We couldn't save your changes yet
      </p>
      <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
        {issues.map((issue, i) => (
          <li key={i}>
            {labelFor(issue.path.join("."))}: {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
